/**
 * Products Service - API calls for products management
 */

const API_BASE = `${import.meta.env.VITE_API_BASE_URL}/products`;

/**
 * En-têtes d'un appel qui écrit.
 *
 * Le serveur vérifie désormais que le produit visé appartient bien à
 * l'appelant. Il lui faut donc une identité prouvée : le JWT signé au login,
 * et non le `peopleId` que le corps ou la query de la requête déclarait
 * jusqu'ici — ce dernier venait du client, et se remplaçait.
 *
 * Le jeton est écrit sous `authToken` par `AuthContext.saveUserToStorage` ;
 * `sessionStorage` prend le relais quand l'utilisateur n'a pas coché « rester
 * connecté ».
 */
function enTetesEcriture(avecCorps = true) {
    let token = null;
    try {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    } catch {
        token = null;
    }

    const headers = {};
    if (avecCorps) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
}

/**
 * Get all product types/categories
 * @param {string} lang - Language code
 * @returns {Promise<Array>} List of product types
 */
export const getProductTypes = async (lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/types?lang=${lang}`);
        if (response.status === 204) return [];
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        return success ? (data.data ?? data.DATA ?? []) : [];
    } catch (error) {
        console.error('Error fetching product types:', error);
        return [];
    }
};

/**
 * Get products with filters
 * @param {Object} params - Filter parameters
 * @returns {Promise<Object>} { data: [], total: 0 }
 */
export const getProducts = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();

        if (params.peopleId) queryParams.append('peopleId', params.peopleId);
        if (params.placeId) queryParams.append('placeId', params.placeId);
        if (params.typeId) queryParams.append('typeId', params.typeId);
        if (params.listingType) queryParams.append('listingType', params.listingType);
        if (params.condition) queryParams.append('condition', params.condition);
        if (params.status) queryParams.append('status', params.status);
        if (params.search) queryParams.append('search', params.search);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.offset) queryParams.append('offset', params.offset);
        if (params.lang) queryParams.append('lang', params.lang);

        const response = await fetch(`${API_BASE}?${queryParams}`, {
            headers: enTetesEcriture(false)
        });
        if (response.status === 204) return { data: [], total: 0 };
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;

        return {
            data: success ? (data.data ?? data.DATA ?? []) : [],
            total: data.total ?? data.TOTAL ?? 0
        };
    } catch (error) {
        console.error('Error fetching products:', error);
        return { data: [], total: 0 };
    }
};

/**
 * Get user's products
 * @param {number} peopleId - User ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} List of products
 */
export const getMyProducts = async (peopleId, lang = 'fr') => {
    const result = await getProducts({ peopleId, lang, status: '' }); // Get all statuses for owner
    return result.data;
};

/**
 * Get single product by UUID
 * @param {string} productUuid - Product UUID
 * @param {string} lang - Language code
 * @param {boolean} incrementView - Whether to increment view count
 * @returns {Promise<Object|null>} Product data or null
 */
export const getProductByUuid = async (productUuid, lang = 'fr', incrementView = false) => {
    try {
        const response = await fetch(
            `${API_BASE}/${productUuid}?lang=${lang}&incrementView=${incrementView}`
        );
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        return success ? (data.data ?? data.DATA) : null;
    } catch (error) {
        console.error('Error fetching product:', error);
        return null;
    }
};

/**
 * Create a new product
 * @param {Object} productData - Product data
 * @returns {Promise<Object>} { success, data, message }
 */
export const createProduct = async (productData) => {
    try {
        const response = await fetch(`${API_BASE}`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify(productData)
        });
        const data = await response.json();
        return {
            success: data.success ?? data.SUCCESS ?? false,
            data: data.data ?? data.DATA ?? {},
            message: data.message ?? data.MESSAGE ?? '',
            // Plafond du plan : lus par lireRefusQuota()
            denyCode: data.denyCode ?? data.DENYCODE,
            current: data.current ?? data.CURRENT,
            max: data.max ?? data.MAX
        };
    } catch (error) {
        console.error('Error creating product:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Update a product
 * @param {string} productUuid - Product UUID
 * @param {Object} updateData - Data to update (must include peopleId)
 * @returns {Promise<Object>} { success, message }
 */
export const updateProduct = async (productUuid, updateData) => {
    try {
        const response = await fetch(`${API_BASE}/${productUuid}`, {
            method: 'PUT',
            headers: enTetesEcriture(),
            body: JSON.stringify(updateData)
        });
        const data = await response.json();
        return {
            success: data.success ?? data.SUCCESS ?? false,
            message: data.message ?? data.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error updating product:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a product (soft delete)
 * @param {string} productUuid - Product UUID
 * @returns {Promise<Object>} { success, message }
 */
export const deleteProduct = async (productUuid) => {
    try {
        const response = await fetch(
            `${API_BASE}/${productUuid}`,
            { method: 'DELETE', headers: enTetesEcriture(false) }
        );
        const data = await response.json();
        return {
            success: data.success ?? data.SUCCESS ?? false,
            message: data.message ?? data.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error deleting product:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Add photo to product
 * @param {string} productUuid - Product UUID
 * @param {number} fileId - File ID from files table
 * @param {boolean} isMain - Whether this is the main photo
 * @returns {Promise<Object>} { success, data, message }
 */
export const addProductPhoto = async (productUuid, fileId, isMain = false) => {
    try {
        const response = await fetch(`${API_BASE}/${productUuid}/photos`, {
            method: 'POST',
            headers: enTetesEcriture(),
            body: JSON.stringify({ fileId, isMain })
        });
        const data = await response.json();
        return {
            success: data.success ?? data.SUCCESS ?? false,
            data: data.data ?? data.DATA ?? {},
            message: data.message ?? data.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error adding product photo:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Remove photo from product
 * @param {string} productUuid - Product UUID
 * @param {number} photoId - Photo ID
 * @returns {Promise<Object>} { success, message }
 */
export const removeProductPhoto = async (productUuid, photoId) => {
    try {
        const response = await fetch(
            `${API_BASE}/${productUuid}/photos/${photoId}`,
            { method: 'DELETE', headers: enTetesEcriture(false) }
        );
        const data = await response.json();
        return {
            success: data.success ?? data.SUCCESS ?? false,
            message: data.message ?? data.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error removing product photo:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Set main photo for product
 * @param {string} productUuid - Product UUID
 * @param {number} photoId - Photo ID to set as main
 * @returns {Promise<Object>} { success, message }
 */
export const setMainPhoto = async (productUuid, photoId) => {
    try {
        const response = await fetch(
            `${API_BASE}/${productUuid}/photos/${photoId}/main`,
            { method: 'PUT', headers: enTetesEcriture(false) }
        );
        const data = await response.json();
        return {
            success: data.success ?? data.SUCCESS ?? false,
            message: data.message ?? data.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error setting main photo:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Mark photo as AI enhanced
 * @param {number} photoId - Photo ID
 * @returns {Promise<Object>} { success, message }
 */
export const markPhotoAiEnhanced = async (photoId) => {
    try {
        const response = await fetch(
            `${API_BASE}/photos/${photoId}/ai-enhanced`,
            { method: 'PUT', headers: enTetesEcriture(false) }
        );
        const data = await response.json();
        return {
            success: data.success ?? data.SUCCESS ?? false,
            message: data.message ?? data.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error marking photo as AI enhanced:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Toggle product favorite
 * @param {string} productUuid - Product UUID
 * @returns {Promise<Object>} { success, isFavorite, message }
 */
export const toggleFavorite = async (productUuid) => {
    try {
        const response = await fetch(`${API_BASE}/${productUuid}/favorite`, {
            method: 'POST',
            headers: enTetesEcriture()
        });
        const data = await response.json();
        return {
            success: data.success ?? data.SUCCESS ?? false,
            isFavorite: data.isFavorite ?? data.ISFAVORITE ?? false,
            message: data.message ?? data.MESSAGE ?? ''
        };
    } catch (error) {
        console.error('Error toggling favorite:', error);
        return { success: false, isFavorite: false, message: error.message };
    }
};

/**
 * Get user's favorite products
 * @param {number} peopleId - User ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} List of favorite products
 */
export const getFavoriteProducts = async (peopleId, lang = 'fr') => {
    try {
        const response = await fetch(
            `${API_BASE}/favorites/${peopleId}?lang=${lang}`,
            { headers: enTetesEcriture(false) }
        );
        const data = await response.json();
        const success = data.success ?? data.SUCCESS;
        return success ? (data.data ?? data.DATA ?? []) : [];
    } catch (error) {
        console.error('Error fetching favorite products:', error);
        return [];
    }
};

/**
 * Update product status
 * @param {string} productUuid - Product UUID
 * @param {string} status - New status (ACTIVE, SOLD, RESERVED, INACTIVE)
 * @returns {Promise<Object>} { success, message }
 */
export const updateProductStatus = async (productUuid, status) => {
    return updateProduct(productUuid, { status });
};

// Status constants
export const PRODUCT_STATUS = {
    ACTIVE: 'ACTIVE',
    SOLD: 'SOLD',
    RESERVED: 'RESERVED',
    INACTIVE: 'INACTIVE',
    DELETED: 'DELETED'
};

// Listing type constants
export const LISTING_TYPE = {
    SELL: 'SELL',
    GIVE: 'GIVE'
};

// Condition constants
export const PRODUCT_CONDITION = {
    NEW: 'NEW',
    USED: 'USED'
};

// Pickup type constants
export const PICKUP_TYPE = {
    ON_SITE: 'ON_SITE',
    IN_STORE: 'IN_STORE',
    BOTH: 'BOTH',
    SHIPPING: 'SHIPPING'
};

// Owner type constants
export const OWNER_TYPE = {
    PERSONAL: 'PERSONAL',
    BUSINESS: 'BUSINESS'
};
